<script setup>
import { ref, computed, defineProps, onMounted, onBeforeUnmount } from 'vue';
import { api } from '../services/api';

defineProps({
    navigate: {
        type: Function,
        required: true
    }
});

const heroStats = [
    { icon: 'star', value: '4.9/5', label: 'Average Rating' },
    { icon: 'shield-checkmark-outline', value: '99.2%', label: 'Visa Approval Rate' },
    { icon: 'cash-outline', value: '$4.2M+', label: 'Student Funds Released' },
    { icon: 'time-outline', value: '48-Hour', label: 'Consultation Response' }
];

const trustedAvatars = ['CO', 'AN', 'AO', 'OA', 'FJ'];

const featuredStats = [
    { value: '$100,000 CAD', label: 'Student Funds Secured' },
    { value: '24 Days', label: 'Visa Approved' },
    { value: '4 Years', label: 'PGWP Pathway' }
];

const VIDEO_FOLDER = 'upload/testimonies';
const RECOMMENDATION_FOLDER = 'upload/recommendations';

const videoStories = ref([]);
const isLoadingVideos = ref(false);
const videosError = ref('');
const playingVideo = ref(-1);
const videoElements = ref([]);
const isVideoExpanded = ref(false);
const collapsedVideoCount = ref(3);

const visibleVideos = computed(() =>
    isVideoExpanded.value ? videoStories.value : videoStories.value.slice(0, collapsedVideoCount.value)
);

const updateCollapsedVideoCount = () => {
    const width = window.innerWidth;
    const next = width >= 1024 ? 3 : width >= 640 ? 2 : 1;
    if (next === collapsedVideoCount.value) return;
    collapsedVideoCount.value = next;
    videoElements.value.forEach((el) => {
        if (el) el.pause();
    });
    videoElements.value = [];
    playingVideo.value = -1;
};

const buildVideoPoster = (url) =>
    url.replace('/video/upload/', '/video/upload/so_0/').replace(/\.[^./]+$/, '.jpg');

async function loadVideos() {
    isLoadingVideos.value = true;
    videosError.value = '';
    try {
        const data = await api.getVideos(VIDEO_FOLDER);
        videoStories.value = (data?.urls || []).map((url) => ({
            url,
            poster: buildVideoPoster(url)
        }));
        videoElements.value = [];
        playingVideo.value = -1;
        isVideoExpanded.value = false;
    } catch (error) {
        videosError.value = error.message || 'Unable to load video testimonials.';
        videoStories.value = [];
    } finally {
        isLoadingVideos.value = false;
    }
}

const toggleVideo = (index) => {
    const element = videoElements.value[index];
    if (!element) return;
    if (element.paused) {
        videoElements.value.forEach((el, i) => {
            if (el && i !== index) el.pause();
        });
        element.play();
        playingVideo.value = index;
    } else {
        element.pause();
        playingVideo.value = -1;
    }
};

const stopVideo = (index) => {
    const element = videoElements.value[index];
    if (element) element.pause();
    playingVideo.value = -1;
};

const toggleVideoExpansion = () => {
    isVideoExpanded.value = !isVideoExpanded.value;
    if (!isVideoExpanded.value) {
        videoElements.value.forEach((el) => {
            if (el) el.pause();
        });
        playingVideo.value = -1;
    }
};

const recommendationVideos = ref([]);
const isLoadingRecommendations = ref(false);
const recommendationsError = ref('');
const playingRecommendation = ref(-1);
const recommendationElements = ref([]);
const isRecommendationsExpanded = ref(false);
const collapsedRecommendationCount = ref(3);

const visibleRecommendations = computed(() =>
    isRecommendationsExpanded.value ? recommendationVideos.value : recommendationVideos.value.slice(0, collapsedRecommendationCount.value)
);

const updateCollapsedRecommendationCount = () => {
    const width = window.innerWidth;
    const next = width >= 1024 ? 3 : width >= 640 ? 2 : 1;
    if (next === collapsedRecommendationCount.value) return;
    collapsedRecommendationCount.value = next;
    recommendationElements.value.forEach((el) => {
        if (el) el.pause();
    });
    recommendationElements.value = [];
    playingRecommendation.value = -1;
};

async function loadRecommendationVideos() {
    isLoadingRecommendations.value = true;
    recommendationsError.value = '';
    try {
        const data = await api.getVideos(RECOMMENDATION_FOLDER);
        recommendationVideos.value = (data?.urls || []).map((url) => ({
            url,
            poster: buildVideoPoster(url)
        }));
        recommendationElements.value = [];
        playingRecommendation.value = -1;
        isRecommendationsExpanded.value = false;
    } catch (error) {
        recommendationsError.value = error.message || 'Unable to load recommendation videos.';
        recommendationVideos.value = [];
    } finally {
        isLoadingRecommendations.value = false;
    }
}

const toggleRecommendationVideo = (index) => {
    const element = recommendationElements.value[index];
    if (!element) return;
    if (element.paused) {
        recommendationElements.value.forEach((el, i) => {
            if (el && i !== index) el.pause();
        });
        element.play();
        playingRecommendation.value = index;
    } else {
        element.pause();
        playingRecommendation.value = -1;
    }
};

const stopRecommendationVideo = (index) => {
    const element = recommendationElements.value[index];
    if (element) element.pause();
    playingRecommendation.value = -1;
};

const toggleRecommendationsExpansion = () => {
    isRecommendationsExpanded.value = !isRecommendationsExpanded.value;
    if (!isRecommendationsExpanded.value) {
        recommendationElements.value.forEach((el) => {
            if (el) el.pause();
        });
        playingRecommendation.value = -1;
    }
};

const handleResize = () => {
    updateCollapsedVideoCount();
    updateCollapsedRecommendationCount();
};

onMounted(() => {
    updateCollapsedVideoCount();
    updateCollapsedRecommendationCount();
    window.addEventListener('resize', handleResize);
    loadVideos();
    loadRecommendationVideos();
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
});

const filters = ['Most Recent', 'Study Permit', 'Post-Graduate Permit', 'Work Permit', 'US', 'SOP & Visa', 'All Stories'];
const activeFilter = ref('Most Recent');

const testimonials = ref([
    {
        avatar: 'CO',
        name: 'Chinedu Okafor',
        date: '2025-08-08',
        category: 'Study Permit',
        rating: 5,
        title: 'From 3x Refusals to a Full Ride at U of T',
        review: 'After three refusals I had almost given up on Canada. The team rebuilt my entire application, rewrote my SOP around my research goals, and secured a scholarship offer. My study permit was approved in 24 days. I still cannot believe I am here.',
        tags: ['Study Permit', 'Scholarship', 'SOP'],
        showFullReview: false
    },
    {
        avatar: 'AO',
        name: 'Amara Ojo',
        date: '2025-07-30',
        category: 'Study Permit',
        rating: 5,
        title: 'CAQ to Study Permit in Record Time',
        review: 'From my CAQ application to the final study permit, everything was handled without a single delay. They knew exactly which documents Quebec wanted and prepared me for the interview. I got my approval email in record time.',
        tags: ['Study Permit', 'CAQ', 'Quebec'],
        showFullReview: false
    },
    {
        avatar: 'AN',
        name: 'Adaeze Nwosu',
        date: '2025-07-21',
        category: 'Post-Graduate Permit',
        rating: 4,
        title: 'PGWP Approved While I Was Still on Campus',
        review: 'I applied for my post-graduation work permit before my final transcripts were even released. Their advisor mapped out the exact timing so I never fell out of status. Approved without a single follow-up request.',
        tags: ['PGWP', 'Work Permit', 'Timeline'],
        showFullReview: false
    },
    {
        avatar: 'OA',
        name: 'Olumide Adeyemo',
        date: '2025-07-14',
        category: 'Work Permit',
        rating: 5,
        title: 'Spousal Open Work Permit in One Attempt',
        review: 'My wife was the student and I needed a spousal open work permit. The document checklist they built was flawless. We submitted together and both permits came back approved on the first attempt.',
        tags: ['Work Permit', 'Spousal', 'Family'],
        showFullReview: false
    },
    {
        avatar: 'FJ',
        name: 'Faith Jefwe',
        date: '2025-07-01',
        category: 'SOP & Visa',
        rating: 5,
        title: 'My SOP Finally Told the Right Story',
        review: 'I had the grades but my previous statement of purpose was generic. They spent hours with me, pulled out the details that mattered, and produced an SOP that genuinely sounded like me. That rewrite changed everything.',
        tags: ['SOP', 'Visa', 'Refusal Recovery'],
        showFullReview: false
    },
    {
        avatar: 'OW',
        name: 'Oben Weli',
        date: '2025-06-22',
        category: 'US',
        rating: 4,
        title: 'B1/B2 Visitor Visa for My Sister\u2019s Graduation',
        review: 'I needed a US visitor visa quickly for a family graduation. They prepared my ties-to-home documentation and coached me through the interview questions. Approved for multiple entries with no stress at all.',
        tags: ['US Visa', 'Visitor', 'Interview'],
        showFullReview: false
    },
    {
        avatar: 'KO',
        name: 'Kelechi Obi',
        date: '2025-06-10',
        category: 'Study Permit',
        rating: 5,
        title: 'SDS Application Without a Single Query',
        review: 'They handled my SDS file end to end, including my GIC and upfront medicals. The application went in clean and came back approved with no procedural fairness letter. Highly recommend for anyone in Africa applying to Canada.',
        tags: ['Study Permit', 'SDS', 'GIC'],
        showFullReview: false
    },
    {
        avatar: 'NE',
        name: 'Ngozi Eze',
        date: '2025-05-28',
        category: 'Post-Graduate Permit',
        rating: 5,
        title: 'Co-Op Work Permit + PGWP, Back to Back',
        review: 'I needed a co-op work permit during my studies and then a PGWP right after graduation. They sequenced both applications perfectly so there was zero gap in my ability to work. Truly expert guidance.',
        tags: ['PGWP', 'Co-Op', 'Work Permit'],
        showFullReview: false
    },
    {
        avatar: 'TB',
        name: 'Tunde Bakare',
        date: '2025-05-15',
        category: 'Work Permit',
        rating: 4,
        title: 'LMIA-Backed Work Permit Sorted in Weeks',
        review: 'My employer provided the LMIA but the paperwork was overwhelming. Swif Study Abroad reviewed the offer, organized my credentials, and filed a work permit that was approved in a matter of weeks.',
        tags: ['Work Permit', 'LMIA', 'Employer'],
        showFullReview: false
    }
]);

const filteredTestimonials = computed(() => {
    if (activeFilter.value === 'Most Recent' || activeFilter.value === 'All Stories') {
        return testimonials.value;
    }
    return testimonials.value.filter((item) => item.category === activeFilter.value);
});

const isExpanded = ref(false);
const collapsedCount = 3;
const visibleTestimonials = computed(() =>
    isExpanded.value ? filteredTestimonials.value : filteredTestimonials.value.slice(0, collapsedCount)
);

const selectFilter = (filter) => {
    activeFilter.value = filter;
    isExpanded.value = false;
};

const guarantees = [
    { icon: 'ribbon-outline', title: 'IRCC Registered Consultants', description: 'Every application is reviewed and signed off by a licensed RCIC professional before it is submitted.' },
    { icon: 'lock-closed-outline', title: 'Zero Hidden Fees & Escrow Security', description: 'Transparent pricing with client funds held securely in escrow until each milestone is met.' },
    { icon: 'pulse-outline', title: 'Real-time IRCC Status Portal', description: 'Follow your file from biometrics to the final decision with live status updates.' }
];

const milestones = [
    { value: '12,400+', label: 'Students Helped to Canada' },
    { value: '140+', label: 'Universities & Programs' },
    { value: '98%', label: 'First-Attempt Approval' },
    { value: '35 Days', label: 'Average Processing Time' }
];
</script>

<template>
    <div class="bg-white text-gray-800">
        <section class="relative overflow-hidden bg-gray-900 text-white">
            <div class="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-900 to-red-900"></div>
            <div class="absolute -top-24 -right-24 w-96 h-96 bg-primary/20 rounded-full blur-3xl"></div>
            <div class="container mx-auto px-4 py-16 md:py-24 relative z-10">
                <div class="max-w-3xl">
                    <span class="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-6">
                        <span class="w-2 h-2 rounded-full bg-primary"></span>
                        Real Students. Real Results
                    </span>
                    <h1 class="font-serif text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-5">
                        Real Students. Life-Changing Journeys to Canada.
                    </h1>
                    <p class="text-base sm:text-lg text-gray-300 max-w-2xl mb-10">
                        Discover how international students navigate competitive Canadian admissions, study permit endorsement, and post-graduation work transitions with confidence.
                    </p>
                </div>

                <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div v-for="stat in heroStats" :key="stat.label" class="bg-white/10 backdrop-blur border border-white/10 rounded-2xl p-5">
                        <ion-icon :name="stat.icon" class="text-primary text-2xl mb-3"></ion-icon>
                        <p class="text-2xl md:text-3xl font-extrabold">{{ stat.value }}</p>
                        <p class="text-xs sm:text-sm text-gray-300 mt-1">{{ stat.label }}</p>
                    </div>
                </div>

                <div class="flex flex-wrap items-center gap-4 mt-10">
                    <div class="flex -space-x-3">
                        <span v-for="person in trustedAvatars" :key="person" class="w-9 h-9 rounded-full border-2 border-gray-900 bg-primary text-white text-xs font-bold flex items-center justify-center">{{ person }}</span>
                    </div>
                </div>

            </div>
        </section>

        <section class="bg-white py-16 md:py-20">
            <div class="container mx-auto px-4">
                <div class="max-w-4xl mx-auto text-center">
                    <span class="text-xs font-bold uppercase tracking-widest text-primary">Featured Success Story</span>

                    <div class="font-serif text-7xl leading-none text-red-200 mt-6 select-none" aria-hidden="true">&ldquo;</div>

                    <blockquote class="font-serif text-xl md:text-2xl text-gray-800 leading-relaxed mt-2">
                        I had applied previously through an agency and my study permit application was refused under Section 216(1) of the IRCC regulations. Swif Study Abroad restructured my SOP, fixed my proof of funds, and filed within 24 days of my enrollment. In just 8 weeks my study permit was approved.
                    </blockquote>

                    <div class="flex items-center justify-center gap-4 mt-8">
                        <div class="w-14 h-14 rounded-full bg-red-50 text-primary font-bold text-lg flex items-center justify-center">CO</div>
                        <div class="text-left">
                            <h2 class="text-lg md:text-xl font-extrabold text-gray-900">Chiamaka Okonkwo</h2>
                            <p class="text-sm text-gray-500">B.Sc. Computer Science &bull; University of Toronto</p>
                        </div>
                    </div>

                    <div class="flex justify-center mt-3">
                        <ion-icon v-for="s in 5" :key="s" name="star" class="text-yellow-400 w-5 h-5"></ion-icon>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10 max-w-2xl mx-auto">
                        <div v-for="item in featuredStats" :key="item.label" class="bg-gray-50 border border-gray-100 rounded-xl p-4 text-center">
                            <p class="text-sm md:text-base font-extrabold text-primary">{{ item.value }}</p>
                            <p class="text-xs text-gray-500 mt-1">{{ item.label }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="relative overflow-hidden bg-gray-50 py-16 md:py-24">
            <div class="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full section-glow blur-3xl"></div>
            <div class="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full section-glow blur-3xl"></div>

            <div class="container mx-auto px-4 relative">
                <div class="mx-auto mb-12 max-w-3xl text-center">
                    <span class="text-xs font-bold uppercase tracking-widest text-primary">Video Testimonials</span>
                    <h2 class="text-3xl md:text-4xl font-extrabold text-gray-800 mt-3">Watch Their Journeys Unfold</h2>
                    <p class="text-gray-600 mt-3">Real, authentic video testimonials from international students sharing their Canadian campus experience. These are unscripted testimonies with students who have already navigated admissions, study permits, and the move abroad. Hear the story behind each visa stamp: the setbacks that tested their resolve, the decisions that made the difference, and the moment it all finally came together.</p>
                </div>

                <div v-if="isLoadingVideos" class="flex flex-col items-center justify-center py-20">
                    <span class="video-spinner" role="status" aria-label="Loading video testimonials"></span>
                    <p class="mt-5 text-sm font-medium tracking-wide text-gray-500">Loading video testimonials…</p>
                </div>

                <div v-else-if="videosError" class="mx-auto max-w-md rounded-2xl border border-gray-100 bg-white p-10 text-center shadow-sm">
                    <p class="text-gray-600">{{ videosError }}</p>
                    <button @click="loadVideos" class="mt-5 inline-flex items-center gap-2 bg-primary text-white font-semibold py-2.5 px-6 rounded-lg shadow-lg hover:bg-[#b02020] transition duration-300">
                        Try Again
                    </button>
                </div>

                <div v-else-if="videoStories.length" class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3">
                    <div v-for="(video, index) in visibleVideos" :key="video.url"
                        class="group relative aspect-[4/5] overflow-hidden rounded-3xl bg-gray-900 shadow-lg ring-1 ring-gray-900/5">
                        <video :ref="(el) => { if (el) videoElements[index] = el; }" :src="video.url" :poster="video.poster"
                            preload="metadata" playsinline :controls="playingVideo === index"
                            class="absolute inset-0 h-full w-full object-cover transition duration-700"
                            :class="playingVideo === index ? 'scale-100' : 'group-hover:scale-[1.03]'"
                            @ended="stopVideo(index)" @pause="playingVideo === index && (playingVideo = -1)"></video>
                        <button v-if="playingVideo !== index" type="button" @click="toggleVideo(index)"
                            :aria-label="`Play video testimonial ${index + 1}`"
                            class="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-gray-900/70 via-gray-900/10 to-transparent transition duration-300 group-hover:from-gray-900/80">
                            <span class="w-16 h-16 rounded-full bg-white/95 text-primary flex items-center justify-center shadow-xl ring-1 ring-white/60 group-hover:scale-110 transition duration-300">
                                <ion-icon name="play" class="text-2xl"></ion-icon>
                            </span>
                        </button>
                    </div>
                </div>

                <div v-else class="text-center py-16">
                    <h3 class="text-xl font-semibold text-gray-700">No video testimonials yet</h3>
                    <p class="text-gray-500 mt-2">Check back soon for new student stories.</p>
                </div>

                <div v-if="!isLoadingVideos && !videosError && videoStories.length > collapsedVideoCount" class="text-center mt-14">
                    <button @click="toggleVideoExpansion" class="inline-flex items-center gap-2 bg-white border border-[#d62828]/30 text-primary font-semibold py-3 px-7 rounded-full shadow-sm hover:bg-[#d62828] hover:text-white transition duration-300">
                        {{ isVideoExpanded ? 'Show Fewer Videos' : `See More Videos (${videoStories.length - collapsedVideoCount})` }}
                        <ion-icon :name="isVideoExpanded ? 'chevron-up' : 'chevron-down'"></ion-icon>
                    </button>
                </div>
            </div>
        </section>

        <section class="bg-white py-16 md:py-20">
            <div class="container mx-auto px-4">
                <div class="flex items-center justify-between gap-4 mb-10">
                    <div class="flex gap-2 overflow-x-auto pb-2">
                        <button v-for="filter in filters" :key="filter" @click="selectFilter(filter)"
                            class="whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border transition duration-300"
                            :class="activeFilter === filter ? 'bg-primary text-white border-primary shadow-md' : 'bg-white text-gray-600 border-gray-200 hover:border-primary hover:text-primary'">
                            {{ filter }}
                        </button>
                    </div>
                </div>

                <div class="text-center max-w-2xl mx-auto mb-12">
                    <span class="text-xs font-bold uppercase tracking-widest text-primary">Authentic Experiences</span>
                    <h2 class="text-3xl md:text-4xl font-extrabold text-gray-800 mt-3">Every Visa Stamp Tells a Milestone</h2>
                    <p class="text-gray-600 mt-3">Unfiltered reviews from students who trusted us with their Canadian journey.</p>
                </div>

                <div v-if="filteredTestimonials.length" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    <article v-for="(testimonial, index) in visibleTestimonials" :key="index" class="bg-white rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 p-6 flex flex-col transition-shadow duration-300">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-red-50 text-primary font-bold flex items-center justify-center">
                                {{ testimonial.avatar }}
                            </div>
                            <div>
                                <p class="font-bold text-primary">{{ testimonial.name }}</p>
                                <p class="text-xs text-gray-500">{{ testimonial.date }} • {{ testimonial.category }}</p>
                            </div>
                        </div>
                        <div class="flex mt-4">
                            <template v-for="star in 5" :key="star">
                                <ion-icon :name="star <= testimonial.rating ? 'star' : 'star-outline'" class="text-yellow-400 w-4 h-4"></ion-icon>
                            </template>
                        </div>
                        <h3 class="font-bold text-gray-900 mt-4">{{ testimonial.title }}</h3>
                        <p class="text-gray-600 text-sm mt-2 flex-grow" :class="{ 'line-clamp-4': !testimonial.showFullReview }">{{ testimonial.review }}</p>
                        <div class="flex flex-wrap gap-2 mt-4">
                            <span v-for="tag in testimonial.tags" :key="tag" class="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">{{ tag }}</span>
                        </div>
                        <button @click="testimonial.showFullReview = !testimonial.showFullReview" class="mt-4 self-start text-primary hover:underline text-sm font-semibold focus:outline-none">
                            {{ testimonial.showFullReview ? 'Read less' : 'Read Full Story →' }}
                        </button>
                    </article>
                </div>
                <div v-else class="text-center py-16">
                    <h3 class="text-xl font-semibold text-gray-700">No stories in this category yet</h3>
                    <p class="text-gray-500 mt-2">Try another filter to see more student experiences.</p>
                </div>

                <div v-if="filteredTestimonials.length > collapsedCount" class="text-center mt-12">
                    <button @click="isExpanded = !isExpanded" class="inline-flex items-center gap-2 bg-white border-2 border-primary text-primary font-bold py-3 px-6 rounded-xl hover:bg-primary hover:text-white transition duration-300 shadow-md">
                        {{ isExpanded ? 'Show Fewer Stories' : `Show All ${filteredTestimonials.length} Stories` }}
                        <ion-icon :name="isExpanded ? 'chevron-up' : 'chevron-down'"></ion-icon>
                    </button>
                </div>
            </div>
        </section>

        <section class="bg-white py-16 md:py-20 border-t border-gray-100">
            <div class="container mx-auto px-4">
                <div class="max-w-3xl mx-auto text-center mb-12">
                    <span class="text-xs font-bold uppercase tracking-widest text-primary">Recommendations</span>
                    <h2 class="text-3xl md:text-4xl font-extrabold text-gray-800 mt-3">Highly Recommended, Every Step of the Way</h2>
                    <p class="text-gray-600 mt-5 leading-relaxed">
                        Swif Study Abroad is the name students, parents, and influencers pass on to the people they trust. Most of our new students reach us through word of mouth: families share our number with other parents, graduates introduce us to younger siblings, and trusted influencers recommend us to their audiences. Being recommended this often is a responsibility we do not take lightly.
                    </p>
                </div>

                <div v-if="isLoadingRecommendations" class="flex flex-col items-center justify-center py-16">
                    <span class="video-spinner" role="status" aria-label="Loading recommendation videos"></span>
                    <p class="mt-5 text-sm font-medium tracking-wide text-gray-500">Loading recommendation videos…</p>
                </div>

                <div v-else-if="recommendationsError" class="mx-auto max-w-md rounded-2xl border border-gray-100 bg-white p-10 text-center shadow-sm">
                    <p class="text-gray-600">{{ recommendationsError }}</p>
                    <button @click="loadRecommendationVideos" class="mt-5 inline-flex items-center gap-2 bg-primary text-white font-semibold py-2.5 px-6 rounded-lg shadow-lg hover:bg-[#b02020] transition duration-300">
                        Try Again
                    </button>
                </div>

                <div v-else-if="recommendationVideos.length" class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3">
                    <div v-for="(video, index) in visibleRecommendations" :key="video.url"
                        class="group relative aspect-[4/5] overflow-hidden rounded-3xl bg-gray-900 shadow-lg ring-1 ring-gray-900/5">
                        <video :ref="(el) => { if (el) recommendationElements[index] = el; }" :src="video.url" :poster="video.poster"
                            preload="metadata" playsinline :controls="playingRecommendation === index"
                            class="absolute inset-0 h-full w-full object-cover transition duration-700"
                            :class="playingRecommendation === index ? 'scale-100' : 'group-hover:scale-[1.03]'"
                            @ended="stopRecommendationVideo(index)" @pause="playingRecommendation === index && (playingRecommendation = -1)"></video>
                        <button v-if="playingRecommendation !== index" type="button" @click="toggleRecommendationVideo(index)"
                            :aria-label="`Play recommendation video ${index + 1}`"
                            class="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-gray-900/70 via-gray-900/10 to-transparent transition duration-300 group-hover:from-gray-900/80">
                            <span class="w-16 h-16 rounded-full bg-white/95 text-primary flex items-center justify-center shadow-xl ring-1 ring-white/60 group-hover:scale-110 transition duration-300">
                                <ion-icon name="play" class="text-2xl"></ion-icon>
                            </span>
                        </button>
                    </div>
                </div>

                <div v-else class="text-center py-16">
                    <h3 class="text-xl font-semibold text-gray-700">No recommendation videos yet</h3>
                    <p class="text-gray-500 mt-2">Check back soon for new student recommendations.</p>
                </div>

                <div v-if="!isLoadingRecommendations && !recommendationsError && recommendationVideos.length > collapsedRecommendationCount" class="text-center mt-14">
                    <button @click="toggleRecommendationsExpansion" class="inline-flex items-center gap-2 bg-white border border-[#d62828]/30 text-primary font-semibold py-3 px-7 rounded-full shadow-sm hover:bg-[#d62828] hover:text-white transition duration-300">
                        {{ isRecommendationsExpanded ? 'Show Fewer Videos' : `See More Videos (${recommendationVideos.length - collapsedRecommendationCount})` }}
                        <ion-icon :name="isRecommendationsExpanded ? 'chevron-up' : 'chevron-down'"></ion-icon>
                    </button>
                </div>
            </div>
        </section>

        <section class="bg-gray-50 py-16">
            <div class="container mx-auto px-4">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    <div v-for="item in guarantees" :key="item.title" class="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
                        <div class="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-5">
                            <ion-icon :name="item.icon" class="text-primary text-2xl"></ion-icon>
                        </div>
                        <h3 class="font-bold text-lg text-gray-900 mb-2">{{ item.title }}</h3>
                        <p class="text-gray-600 text-sm">{{ item.description }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="bg-gray-900 py-14">
            <div class="container mx-auto px-4">
                <div class="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center text-white">
                    <div v-for="item in milestones" :key="item.label">
                        <p class="text-3xl md:text-4xl font-extrabold text-primary">{{ item.value }}</p>
                        <p class="text-sm text-gray-300 mt-2">{{ item.label }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="bg-primary py-16 md:py-20">
            <div class="container mx-auto px-4 text-center text-white">
                <span class="text-xs font-bold uppercase tracking-widest text-white/80">Your Turn</span>
                <h2 class="font-serif text-3xl md:text-5xl font-bold mt-4 mb-5">Ready to Write Your Canadian Success Story?</h2>
                <p class="text-white/90 max-w-2xl mx-auto mb-10">Join thousands of international students who turned a daunting visa process into a confident, well-supported journey.</p>
                <div class="flex flex-col sm:flex-row justify-center gap-4">
                    <button @click="navigate('scheduling')" class="bg-white text-primary font-bold py-3 px-8 rounded-lg shadow-lg hover:bg-gray-100 transition duration-300">Check My Eligibility Now</button>
                    <button @click="navigate('scheduling')" class="bg-gray-900 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:bg-gray-800 transition duration-300">Book 1-on-1 Consultation</button>
                </div>
            </div>
        </section>
    </div>
</template>

<style scoped>
section .overflow-x-auto::-webkit-scrollbar {
    height: 6px;
}

section .overflow-x-auto::-webkit-scrollbar-thumb {
    background-color: #d1d5db;
    border-radius: 9999px;
}

.video-spinner {
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 9999px;
    border: 4px solid rgba(214, 40, 40, 0.2);
    border-top-color: #d62828;
    animation: spin 0.9s linear infinite;
}

.section-glow {
    background-color: rgba(214, 40, 40, 0.05);
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .video-spinner {
        animation-duration: 2.5s;
    }
}
</style>
